import { useEffect, useRef, useState } from 'react';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { logger } from 'src/logger';

// Backstop: never leave hash mirroring paused indefinitely if the app never reports the exact
// target (e.g. a non-protocol app that never reports at all, or path normalization on the child).
const RESTORE_FALLBACK_MS = 4000;

/**
 * Deep-link restore coordination.
 *
 * The deep-link path from the FinSuite URL hash is baked directly into the iframe `src` (see
 * EmbedApp), so the embedded app normally loads straight at the target — no command needed, and
 * it works even for apps without postMessage support. This hook handles only the two remaining
 * jobs, both driven by the app's `CURRENT_PATH` reports:
 *
 *   1. Pause hash mirroring (`restorePending`) until the app confirms it is at the target, so a
 *      stray initial report (e.g. the app fell back to its home page) can't clobber the pending
 *      deep-link hash.
 *   2. Fallback only: if the app reports a path OTHER than the target — meaning the src-baked load
 *      did not take and home opened — send a one-shot FINSUITE_NAVIGATE_TO to drive it there. In
 *      the common (successful) case the first report equals the target, so NAVIGATE_TO is never
 *      sent. Protocol apps only; a non-protocol app never reports and relies purely on the
 *      src-baked load.
 *
 * Pending clears when the app reports the target, with a short timeout as a backstop.
 *
 * @param restoreTarget - Sanitized deep-link path captured from the URL hash at mount, or undefined.
 * @param reportedPath - Latest path the app has reported (or null).
 * @returns Whether a deep-link restore is still pending (mirroring should stay paused).
 */
export const useEmbedDeepLinkRestore = (restoreTarget: string | undefined, reportedPath: string | null): boolean => {
  const [restorePending, setRestorePending] = useState<boolean>(() => !!restoreTarget);
  const fallbackSentRef = useRef(false);

  useEffect(() => {
    if (!restorePending || !restoreTarget || !reportedPath) return;

    if (reportedPath === restoreTarget) {
      // At the target — the src-baked load worked (or the fallback completed). Resume mirroring.
      setRestorePending(false);
      return;
    }

    // The app reported a different path (it opened its home page instead of the deep link). Drive
    // it to the target once. Pending stays set until the app then reports the target.
    if (!fallbackSentRef.current) {
      fallbackSentRef.current = true;
      postMessageToEmbeddedIframe(PARENT_TO_EMBED_MESSAGES.NAVIGATE_TO, { path: restoreTarget });
      logger.info(`Deep-link restore fallback: sent FINSUITE_NAVIGATE_TO ${restoreTarget}`);
    }
  }, [restorePending, restoreTarget, reportedPath]);

  // Backstop: if the app keeps reporting paths but never the exact target (e.g. path
  // normalization), resume mirroring after a window of report inactivity. Armed only AFTER the
  // first report and reset on each report, so it measures time-since-last-report. A slow cold
  // start (no reports yet) never trips it — otherwise `restorePending` could clear before the
  // first report and let a home report clobber the deep-link hash.
  useEffect(() => {
    if (!restorePending || !reportedPath) return;
    const id = setTimeout(() => setRestorePending(false), RESTORE_FALLBACK_MS);
    return () => clearTimeout(id);
  }, [restorePending, reportedPath]);

  return restorePending;
};
